import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import MfaEnforcementGate from "@/components/MfaEnforcementGate";
import api from "@/lib/api";
import Login from "@/pages/Login";
import ResetPasswordPage from "@/pages/auth/ResetPasswordPage";
import Maintenance from "@/pages/Maintenance";
import VerifySignature from "@/pages/VerifySignature";
import Dashboard from "@/pages/Dashboard";
import SPBList from "@/pages/SPBList";
import SPBCreate from "@/pages/SPBCreate";
import SPBDetail from "@/pages/SPBDetail";
import SPBEdit from "@/pages/SPBEdit";
import Districts from "@/pages/Districts";
import Products from "@/pages/Products";
import Trucks from "@/pages/Trucks";
import Plants from "@/pages/Plants";
import Customers from "@/pages/Customers";
import Users from "@/pages/Users";
import SignatureLibrary from "@/pages/SignatureLibrary";
import SalesOrders from "@/pages/SalesOrders";
import LoadingOps from "@/pages/LoadingOps";
import SheetsSync from "@/pages/SheetsSync";
import Settings from "@/pages/Settings";
import Audit from "@/pages/Audit";
import LayoutEditor from "@/pages/LayoutEditor";
import WhatsAppSettings from "@/pages/WhatsAppSettings";
import SlotCalendar from "@/pages/SlotCalendar";
import SOUsageReport from "@/pages/SOUsageReport";
import SORemainingReport from "@/pages/SORemainingReport";
import SPBReport from "@/pages/SPBReport";
import DistrikMonthlyReport from "@/pages/DistrikMonthlyReport";
import DailyProofReport from "@/pages/DailyProofReport";
import SyncMatchReport from "@/pages/SyncMatchReport";
import Integrations from "@/pages/Integrations";
import MismatchApprovals from "@/pages/MismatchApprovals";

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  // Both admin roles land on Dashboard; petugas goes to Cek Validasi (their only screen).
  if (user.role === "admin" || user.role === "admin_kantor") return <Dashboard />;
  return <Navigate to="/loading" replace />;
}

// Global gate: polls /api/public/maintenance and, when enabled, forces
// every route EXCEPT `/maintenance`, `/login`, and public verify pages to
// show the Maintenance screen. Full admins (role="admin") are exempt so
// they can still access the app to turn maintenance mode off from Settings.
function MaintenanceGate({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    let alive = true;
    const check = async () => {
      try {
        const { data } = await api.get("/public/maintenance");
        if (alive) setEnabled(!!data?.enabled);
      } catch {
        // On network failure we assume NOT in maintenance so the app
        // stays usable — a real deploy will make the poll succeed with
        // enabled=true within 20 seconds.
      }
    };
    check();
    const t = setInterval(check, 30000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const bypassPaths = ["/maintenance", "/login"];
  const isPublicVerify = location.pathname.startsWith("/v/") || location.pathname.startsWith("/verify-signature/");
  const shouldBypass = bypassPaths.includes(location.pathname) || isPublicVerify;

  // Full admin keeps working — they need to disable the flag when done.
  if (enabled && user?.role !== "admin" && !shouldBypass) {
    return <Navigate to="/maintenance" replace />;
  }
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <MfaEnforcementGate />
        <MaintenanceGate>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          {/* Public maintenance page — accessible without auth */}
          <Route path="/maintenance" element={<Maintenance />} />
          {/* Public verify page — accessible without auth */}
          <Route path="/verify-signature/:token" element={<VerifySignature />} />
          <Route path="/v/:sid/:nonce" element={<VerifySignature />} />
          <Route path="/" element={<ProtectedRoute><RootRedirect /></ProtectedRoute>} />
          <Route path="/spb" element={<ProtectedRoute><SPBList /></ProtectedRoute>} />
          <Route path="/spb/new" element={<ProtectedRoute adminOnly><SPBCreate /></ProtectedRoute>} />
          <Route path="/spb/:id" element={<ProtectedRoute><SPBDetail /></ProtectedRoute>} />
          <Route path="/spb/:id/edit" element={<ProtectedRoute><SPBEdit /></ProtectedRoute>} />
          <Route path="/loading" element={<ProtectedRoute><LoadingOps /></ProtectedRoute>} />
          <Route path="/districts" element={<ProtectedRoute adminOnly><Districts /></ProtectedRoute>} />
          <Route path="/products" element={<ProtectedRoute adminOnly><Products /></ProtectedRoute>} />
          <Route path="/trucks" element={<ProtectedRoute adminOnly><Trucks /></ProtectedRoute>} />
          <Route path="/plants" element={<ProtectedRoute adminOnly><Plants /></ProtectedRoute>} />
          <Route path="/customers" element={<ProtectedRoute adminOnly><Customers /></ProtectedRoute>} />
          <Route path="/users" element={<ProtectedRoute fullAdminOnly><Users /></ProtectedRoute>} />
          <Route path="/settings/signatures" element={<ProtectedRoute fullAdminOnly><SignatureLibrary /></ProtectedRoute>} />
          <Route path="/sales-orders" element={<ProtectedRoute adminOnly><SalesOrders /></ProtectedRoute>} />
          <Route path="/sheets" element={<ProtectedRoute adminOnly><SheetsSync /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute adminOnly><Settings /></ProtectedRoute>} />
          <Route path="/settings/whatsapp" element={<ProtectedRoute fullAdminOnly><WhatsAppSettings /></ProtectedRoute>} />
          <Route path="/slot-calendar" element={<ProtectedRoute adminOnly><SlotCalendar /></ProtectedRoute>} />
          <Route path="/layout" element={<ProtectedRoute fullAdminOnly><LayoutEditor /></ProtectedRoute>} />
          <Route path="/audit" element={<ProtectedRoute adminOnly><Audit /></ProtectedRoute>} />
          <Route path="/reports/so-usage" element={<ProtectedRoute adminOnly><SOUsageReport /></ProtectedRoute>} />
          <Route path="/reports/so-remaining" element={<ProtectedRoute adminOnly><SORemainingReport /></ProtectedRoute>} />
          <Route path="/reports/spb" element={<ProtectedRoute adminOnly><SPBReport /></ProtectedRoute>} />
          <Route path="/reports/distrik-monthly" element={<ProtectedRoute adminOnly><DistrikMonthlyReport /></ProtectedRoute>} />
          <Route path="/reports/bukti-muat-harian" element={<ProtectedRoute adminOnly><DailyProofReport /></ProtectedRoute>} />
          <Route path="/reports/sync-match" element={<ProtectedRoute adminOnly><SyncMatchReport /></ProtectedRoute>} />
          <Route path="/settings/integrations" element={<ProtectedRoute fullAdminOnly><Integrations /></ProtectedRoute>} />
          <Route path="/mismatch-corrections" element={<ProtectedRoute adminOnly><MismatchApprovals /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </MaintenanceGate>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
